import { JSONSchema, MVELValidationResult } from '../components/mvel-editor/types';

// MVEL keywords and operators
export const MVEL_KEYWORDS = [
  'if',
  'else',
  'foreach',
  'while',
  'do',
  'for',
  'in',
  'switch',
  'case',
  'default',
  'return',
  'break',
  'continue',
  'try',
  'catch',
  'finally',
  'throw',
  'new',
  'import',
  'function',
  'def',
  'var',
  'null',
  'true',
  'false',
  'this',
  'super',
  'instanceof',
  'typeof',
  'class',
  'extends',
  'implements',
  'interface',
  'abstract',
  'final',
  'static',
  'public',
  'private',
  'protected',
  'synchronized'
];

export const MVEL_OPERATORS = [
  '+',
  '-',
  '*',
  '/',
  '%',
  '=',
  '==',
  '!=',
  '<',
  '>',
  '<=',
  '>=',
  '&&',
  '||',
  '!',
  '&',
  '|',
  '^',
  '~',
  '<<',
  '>>',
  '>>>',
  '+=',
  '-=',
  '*=',
  '/=',
  '%=',
  '&=',
  '|=',
  '^=',
  '<<=',
  '>>=',
  '>>>=',
  '++',
  '--',
  '?',
  ':',
  '.',
  '[',
  ']',
  '(',
  ')',
  '{',
  '}',
  ';',
  ','
];

export const MVEL_FUNCTIONS = [
  'isEmpty',
  'isNotEmpty',
  'length',
  'size',
  'contains',
  'startsWith',
  'endsWith',
  'indexOf',
  'substring',
  'toLowerCase',
  'toUpperCase',
  'trim',
  'split',
  'join',
  'toString',
  'toNumber',
  'toDate',
  'format',
  'parse',
  'abs',
  'ceil',
  'floor',
  'round',
  'max',
  'min',
  'sum',
  'avg',
  'count',
  'sort',
  'reverse',
  'first',
  'last'
];

// Extract property paths from JSON schema for intellisense
export function extractSchemaProperties(schema: JSONSchema, prefix = ''): string[] {
  const properties: string[] = [];

  if (!schema || !schema.properties) return properties;

  for (const [key, value] of Object.entries(schema.properties)) {
    const fullPath = prefix ? `${prefix}.${key}` : key;
    properties.push(fullPath);

    if (value.type === 'object' && value.properties) {
      properties.push(...extractSchemaProperties(value, fullPath));
    } else if (value.type === 'array' && value.items && typeof value.items === 'object') {
      properties.push(`${fullPath}[0]`);
      if (value.items.properties) {
        const arrayItemProps = extractSchemaProperties(value.items, `${fullPath}[0]`);
        properties.push(...arrayItemProps);
      }
    }
  }

  return properties;
}

// Basic MVEL syntax validation
export function validateMVELSyntax(script: string): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!script || script.trim() === '') {
    return { isValid: true, errors: [] };
  }

  // Check for balanced parentheses
  const openParens = (script.match(/\(/g) || []).length;
  const closeParens = (script.match(/\)/g) || []).length;
  if (openParens !== closeParens) {
    errors.push('Unbalanced parentheses');
  }

  // Check for balanced brackets
  const openBrackets = (script.match(/\[/g) || []).length;
  const closeBrackets = (script.match(/\]/g) || []).length;
  if (openBrackets !== closeBrackets) {
    errors.push('Unbalanced square brackets');
  }

  // Check for balanced braces
  const openBraces = (script.match(/\{/g) || []).length;
  const closeBraces = (script.match(/\}/g) || []).length;
  if (openBraces !== closeBraces) {
    errors.push('Unbalanced curly braces');
  }

  // Check for unterminated strings
  const singleQuotes = (script.match(/'/g) || []).length;
  const doubleQuotes = (script.match(/"/g) || []).length;
  if (singleQuotes % 2 !== 0) {
    errors.push('Unterminated single-quoted string');
  }
  if (doubleQuotes % 2 !== 0) {
    errors.push('Unterminated double-quoted string');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

// Simulate MVEL execution (basic implementation)
export function executeMVEL(script: string, context: any): MVELValidationResult {
  try {
    const syntaxCheck = validateMVELSyntax(script);
    if (!syntaxCheck.isValid) {
      return {
        isValid: false,
        errors: syntaxCheck.errors
      };
    }

    // Basic MVEL expression evaluation
    // This is a simplified implementation - in a real scenario, you'd use a proper MVEL interpreter
    let result = evaluateBasicExpression(script, context);

    return {
      isValid: true,
      errors: [],
      result
    };
  } catch (error) {
    return {
      isValid: false,
      errors: [error instanceof Error ? error.message : 'Unknown execution error']
    };
  }
}

// Basic expression evaluator (simplified MVEL subset)
function evaluateBasicExpression(expression: string, context: any): any {
  // Remove whitespace
  expression = expression.trim();

  // Handle simple property access like "user.name"
  if (/^[a-zA-Z_][a-zA-Z0-9_]*(\.[a-zA-Z_][a-zA-Z0-9_]*)*$/.test(expression)) {
    return getNestedProperty(context, expression);
  }

  // Handle string literals
  if (expression.startsWith('"') && expression.endsWith('"')) {
    return expression.slice(1, -1);
  }

  if (expression.startsWith("'") && expression.endsWith("'")) {
    return expression.slice(1, -1);
  }

  // Handle numbers
  if (/^\d+(\.\d+)?$/.test(expression)) {
    return parseFloat(expression);
  }

  // Handle booleans
  if (expression === 'true') return true;
  if (expression === 'false') return false;
  if (expression === 'null') return null;

  // Handle simple arithmetic (basic implementation)
  if (
    expression.includes('+') ||
    expression.includes('-') ||
    expression.includes('*') ||
    expression.includes('/')
  ) {
    return evaluateArithmetic(expression, context);
  }

  // Default fallback
  return expression;
}

function getNestedProperty(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
}

function evaluateArithmetic(expression: string, context: any): any {
  // Very basic arithmetic evaluation - replace with proper parser in production
  try {
    // Replace property references with values
    let processedExp = expression;
    const propertyRegex = /[a-zA-Z_][a-zA-Z0-9_]*(\.[a-zA-Z_][a-zA-Z0-9_]*)*/g;
    processedExp = processedExp.replace(propertyRegex, (match) => {
      const value = getNestedProperty(context, match);
      return typeof value === 'number' ? value.toString() : '0';
    });

    // Basic evaluation (unsafe - use a proper expression parser in production)
    return eval(processedExp);
  } catch {
    return null;
  }
}
